/**
 * Rate Limit Context
 * Provides global rate limit state management
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { useRateLimit, RateLimitInfo, RateLimitStatus } from '../hooks/useRateLimit';

interface RateLimitContextType {
  rateLimitInfo: RateLimitInfo | null;
  status: RateLimitStatus;
  timeUntilReset: number;
  shouldShowWarning: () => boolean;
  getStatusColor: () => string;
  getStatusMessage: () => string;
  getTimeUntilResetText: () => string;
  updateFromResponse: (headers: Record<string, string>) => void;
  handleRateLimitError: (error: any) => { isRateLimited: boolean; retryAfter?: number; message?: string };
}

const RateLimitContext = createContext<RateLimitContextType | undefined>(undefined);

interface RateLimitProviderProps {
  children: ReactNode;
}

export const RateLimitProvider: React.FC<RateLimitProviderProps> = ({ children }) => {
  const rateLimitState = useRateLimit();

  return (
    <RateLimitContext.Provider value={rateLimitState}>
      {children}
    </RateLimitContext.Provider>
  );
};

/**
 * Hook to access rate limit context
 */
export const useRateLimitContext = (): RateLimitContextType => {
  const context = useContext(RateLimitContext);
  if (context === undefined) {
    throw new Error('useRateLimitContext must be used within a RateLimitProvider');
  }
  return context;
};

export default RateLimitProvider;